// API helpers for the in-app builder.
//
// Routes are JWT-protected and tenant-scoped; the JWT only carries uid, so
// every call must explicitly pass companyId (query param for GET/DELETE,
// body field for POST/PATCH/PUT).

import axios from "axios";
import { hostname } from "../../utils";
import type {
  BuilderApp,
  BuilderChatAttachment,
  BuilderChatMessage,
  BuilderFile,
  BuilderFileEntry,
  BuilderThreadSummary,
  BuilderThreadsView,
  FlexSchema,
  GitIntegrationSummary,
  GitLinkInput,
  GitProvider,
  GitRepoSummary,
  GitSyncStartResponse,
  GitSyncStatus,
  PublishStatusResponse,
} from "./types";

type GetToken = () => Promise<string>;

export interface BuilderAppsPage {
  apps: BuilderApp[];
  total: number;
  limit: number;
  offset: number;
}

export interface BuilderApiClient {
  listApps(params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<BuilderAppsPage>;
  createApp(input: {
    name: string;
    description?: string;
    icon?: string;
    appType?: string;
    // First user prompt from the apps-list hero. When supplied with a
    // placeholder name, the backend synchronously generates a real name
    // before creating the app so the editor opens with the correct title.
    seedPrompt?: string;
  }): Promise<BuilderApp>;
  getApp(id: string): Promise<BuilderApp>;
  patchApp(id: string, patch: Partial<BuilderApp>): Promise<BuilderApp>;
  deleteApp(id: string): Promise<void>;

  listFiles(appId: string): Promise<BuilderFileEntry[]>;
  readFile(appId: string, path: string): Promise<BuilderFile & { path: string }>;
  writeFile(appId: string, path: string, content: string, type?: string): Promise<void>;
  deleteFile(appId: string, path: string): Promise<void>;
  exportApp(appId: string): Promise<Blob>;

  listThreads(appId: string): Promise<BuilderThreadsView>;
  getThreadMessages(appId: string, threadId: string): Promise<BuilderChatMessage[]>;
  createThread(appId: string, title?: string): Promise<BuilderThreadSummary>;
  renameThread(appId: string, threadId: string, title: string): Promise<BuilderThreadSummary>;
  deleteThread(appId: string, threadId: string): Promise<BuilderThreadsView>;
  setActiveThread(appId: string, threadId: string): Promise<BuilderThreadsView>;

  listFlexSchemas(appId?: string): Promise<FlexSchema[]>;

  setMenuAppIds(appIds: string[]): Promise<string[]>;

  // Returns the underlying fetch Response so the caller can stream the body.
  // Thread history is loaded server-side from the DB; the client only needs
  // to send the new user message and the target threadId.
  chat(
    appId: string,
    body: {
      message: string;
      threadId?: string;
      clientMessageId?: string;
      attachments?: BuilderChatAttachment[];
    },
    signal?: AbortSignal,
  ): Promise<Response>;

  uploadImage(appId: string, file: File): Promise<BuilderChatAttachment>;

  // --- Publish (remote build + CDN hosting) ---
  publishApp(appId: string): Promise<{ ok: true }>;
  getPublishStatus(appId: string): Promise<PublishStatusResponse>;

  // --- Git provider sync ---
  getGitStatus(appId: string): Promise<GitSyncStatus>;
  listGitIntegrations(appId: string, provider?: GitProvider): Promise<GitIntegrationSummary[]>;
  listGitRepos(appId: string, integrationId: string, provider?: GitProvider): Promise<GitRepoSummary[]>;
  linkGitRepo(appId: string, input: GitLinkInput): Promise<GitSyncStatus>;
  unlinkGitRepo(appId: string): Promise<GitSyncStatus>;
  pushToGit(appId: string): Promise<GitSyncStartResponse>;
  pullFromGit(appId: string): Promise<GitSyncStartResponse>;
}

export function createBuilderApi(getToken: GetToken, companyId: string): BuilderApiClient {
  async function authHeaders() {
    const token = await getToken();
    return { Authorization: `Bearer ${token}` };
  }
  const params = { companyId };

  return {
    async listApps(opts) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/apps`, {
        headers,
        params: {
          ...params,
          ...(opts?.search ? { search: opts.search } : {}),
          ...(opts?.limit !== undefined ? { limit: opts.limit } : {}),
          ...(opts?.offset !== undefined ? { offset: opts.offset } : {}),
        },
      });
      return {
        apps: r.data.apps || [],
        total: r.data.total ?? (r.data.apps || []).length,
        limit: r.data.limit ?? 24,
        offset: r.data.offset ?? 0,
      };
    },

    async createApp(input) {
      const headers = await authHeaders();
      const r = await axios.post(`${hostname}/apps`, { ...input, companyId }, { headers });
      return r.data;
    },

    async getApp(id) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/apps/${id}`, { headers, params });
      return r.data;
    },

    async patchApp(id, patch) {
      const headers = await authHeaders();
      const r = await axios.patch(`${hostname}/apps/${id}`, { ...patch, companyId }, { headers });
      return r.data;
    },

    async deleteApp(id) {
      const headers = await authHeaders();
      await axios.delete(`${hostname}/apps/${id}`, { headers, params });
    },

    async listFiles(appId) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/apps/${appId}/files`, { headers, params });
      return r.data.files || [];
    },

    async readFile(appId, path) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/apps/${appId}/file`, {
        headers,
        params: { ...params, path },
      });
      return r.data;
    },

    async writeFile(appId, path, content, type) {
      const headers = await authHeaders();
      await axios.put(
        `${hostname}/apps/${appId}/file`,
        { companyId, path, content, type },
        { headers },
      );
    },

    async deleteFile(appId, path) {
      const headers = await authHeaders();
      await axios.delete(`${hostname}/apps/${appId}/file`, {
        headers,
        params: { ...params, path },
      });
    },

    async exportApp(appId) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/apps/${appId}/export`, {
        headers,
        params,
        responseType: "blob",
      });
      return r.data as Blob;
    },

    async listThreads(appId) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/apps/${appId}/threads`, {
        headers,
        params,
      });
      return r.data;
    },

    async getThreadMessages(appId, threadId) {
      const headers = await authHeaders();
      const r = await axios.get(
        `${hostname}/apps/${appId}/threads/${threadId}`,
        { headers, params },
      );
      return r.data?.messages || [];
    },

    async createThread(appId, title) {
      const headers = await authHeaders();
      const r = await axios.post(
        `${hostname}/apps/${appId}/threads`,
        { companyId, title },
        { headers },
      );
      return r.data;
    },

    async renameThread(appId, threadId, title) {
      const headers = await authHeaders();
      const r = await axios.patch(
        `${hostname}/apps/${appId}/threads/${threadId}`,
        { companyId, title },
        { headers },
      );
      return r.data;
    },

    async deleteThread(appId, threadId) {
      const headers = await authHeaders();
      const r = await axios.delete(
        `${hostname}/apps/${appId}/threads/${threadId}`,
        { headers, params },
      );
      return r.data;
    },

    async setActiveThread(appId, threadId) {
      const headers = await authHeaders();
      const r = await axios.patch(
        `${hostname}/apps/${appId}/threads/${threadId}`,
        { companyId, active: true },
        { headers },
      );
      return r.data;
    },

    async listFlexSchemas(appId) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/flex/schemas`, {
        headers,
        params: appId ? { ...params, appId } : params,
      });
      return r.data.schemas || [];
    },

    async setMenuAppIds(appIds) {
      const headers = await authHeaders();
      const r = await axios.put(
        `${hostname}/apps/menu`,
        { companyId, appIds },
        { headers },
      );
      return r.data?.appIds || [];
    },

    async chat(appId, body, signal) {
      const token = await getToken();
      return fetch(`${hostname}/apps/${appId}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ companyId, ...body }),
        signal,
      });
    },

    async uploadImage(appId, file) {
      const headers = await authHeaders();
      const form = new FormData();
      form.append("file", file);
      form.append("companyId", companyId);
      const r = await axios.post(`${hostname}/apps/${appId}/upload`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        params: { companyId },
      });
      return r.data as BuilderChatAttachment;
    },

    async getGitStatus(appId) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/apps/${appId}/git/status`, {
        headers,
        params,
      });
      return r.data as GitSyncStatus;
    },

    async listGitIntegrations(appId, provider) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/apps/${appId}/git/integrations`, {
        headers,
        params: { ...params, ...(provider ? { provider } : {}) },
      });
      return (r.data?.integrations || []) as GitIntegrationSummary[];
    },

    async listGitRepos(appId, integrationId, provider) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/apps/${appId}/git/repos`, {
        headers,
        params: { ...params, integrationId, ...(provider ? { provider } : {}) },
      });
      return (r.data?.repos || []) as GitRepoSummary[];
    },

    async linkGitRepo(appId, input) {
      const headers = await authHeaders();
      const r = await axios.post(
        `${hostname}/apps/${appId}/git/link`,
        { companyId, ...input },
        { headers },
      );
      return r.data as GitSyncStatus;
    },

    async unlinkGitRepo(appId) {
      const headers = await authHeaders();
      const r = await axios.delete(`${hostname}/apps/${appId}/git/link`, {
        headers,
        params,
      });
      return r.data as GitSyncStatus;
    },

    async pushToGit(appId) {
      const headers = await authHeaders();
      const r = await axios.post(
        `${hostname}/apps/${appId}/git/sync`,
        { companyId },
        { headers },
      );
      return r.data as GitSyncStartResponse;
    },

    async pullFromGit(appId) {
      const headers = await authHeaders();
      const r = await axios.post(
        `${hostname}/apps/${appId}/git/pull`,
        { companyId },
        { headers },
      );
      return r.data as GitSyncStartResponse;
    },

    async publishApp(appId) {
      const headers = await authHeaders();
      const r = await axios.post(
        `${hostname}/apps/${appId}/publish`,
        { companyId },
        { headers },
      );
      return r.data;
    },

    async getPublishStatus(appId) {
      const headers = await authHeaders();
      const r = await axios.get(`${hostname}/apps/${appId}/publish-status`, {
        headers,
        params,
      });
      return r.data as PublishStatusResponse;
    },
  };
}
